(function () {
  const mapEl = document.getElementById("retailer-map");
  const listEl = document.getElementById("retailer-list-scroll");
  const DESKTOP_LIST = window.matchMedia("(min-width: 769px)");
  const GEO_CACHE_KEY = "sf-retailer-geocode-v1";
  // Only used as a fallback when metaobjects lack lat/lng.
  const GEO_DELAY_MS = 200;
  const GEO_CONCURRENCY = 4;

  const STATE_NAMES = {
    CA: "California",
    ID: "Idaho",
    MT: "Montana",
    OR: "Oregon",
    WA: "Washington",
  };

  function toNumber(value) {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  function slug(value) {
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  if (!mapEl || typeof L === "undefined") return;

  const seed = Array.isArray(window.SunFrogRetailers) ? window.SunFrogRetailers : [];

  if (!seed.length) {
    mapEl.innerHTML =
      '<p class="retailer-map__empty">Retailer locations are unavailable right now.</p>';
    return;
  }

  initMap(seed);

  function initMap(seedItems) {
  const map = L.map(mapEl, {
    zoomControl: false,
    scrollWheelZoom: true,
  });

  L.control.zoom({ position: "bottomright" }).addTo(map);

  // OSM tiles — Carto basemaps now require an API key
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    subdomains: "abc",
    maxZoom: 19,
  }).addTo(map);

  map.setView([44.5, -120.5], 6);

  const icon = L.divIcon({
    className: "retailer-marker",
    html: "<span></span>",
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

  const cluster =
    typeof L.markerClusterGroup === "function"
      ? L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 50 })
      : null;

  if (cluster) map.addLayer(cluster);

  const markersById = new Map();
  const listButtonsById = new Map();
  let activeId = null;
  let retailers = [];

  // Show list immediately from seed data; plot map as coords resolve.
  const seedRetailers = seedItems.map((item) => ({
    id: item.id || slug(item.name || item.address),
    name: item.name || item.address,
    address: item.address,
    lat: toNumber(item.lat),
    lng: toNumber(item.lng),
  }));
  buildRetailerList(seedRetailers);

  const readyNow = seedRetailers.filter((r) => r.lat != null && r.lng != null);
  if (readyNow.length) {
    retailers = readyNow;
    plotRetailers(retailers);
    fitAllRetailers();
  } else {
    mapEl.classList.add("is-geocoding");
    mapEl.setAttribute("aria-busy", "true");
  }

  resolveRetailers(seedRetailers)
    .then((resolved) => {
      retailers = resolved.filter((r) => r.lat != null && r.lng != null);
      window.SunFrogRetailers = retailers;

      if (!retailers.length) {
        mapEl.innerHTML =
          '<p class="retailer-map__empty">Retailer locations are unavailable right now.</p>';
        return;
      }

      buildRetailerList(retailers);
      markersById.clear();
      if (cluster) cluster.clearLayers();
      plotRetailers(retailers);
      fitAllRetailers();
    })
    .catch(() => {
      if (!retailers.length) {
        mapEl.innerHTML =
          '<p class="retailer-map__empty">Retailer locations are unavailable right now.</p>';
      }
    })
    .finally(() => {
      mapEl.classList.remove("is-geocoding");
      mapEl.removeAttribute("aria-busy");
      requestAnimationFrame(() => map.invalidateSize());
    });

  window.addEventListener("resize", () => {
    map.invalidateSize();
    if (!activeId && retailers.length) fitAllRetailers();
  });

  DESKTOP_LIST.addEventListener("change", () => {
    map.invalidateSize();
    if (!activeId && retailers.length) fitAllRetailers();
  });

  async function resolveRetailers(items) {
    const cache = readGeoCache();
    const out = [];
    const pending = [];

    for (const item of items) {
      const retailer = {
        id: item.id || slug(item.name || item.address),
        name: item.name || item.address,
        address: item.address,
        lat: toNumber(item.lat),
        lng: toNumber(item.lng),
      };

      if (retailer.lat != null && retailer.lng != null) {
        out.push(retailer);
        continue;
      }

      const cached = cache[cacheKey(retailer.address)];
      if (cached && cached.lat != null && cached.lng != null) {
        retailer.lat = cached.lat;
        retailer.lng = cached.lng;
        out.push(retailer);
        continue;
      }

      pending.push(retailer);
    }

    // Fast path: all coords already on metaobjects / in localStorage.
    if (!pending.length) return out;

    for (let i = 0; i < pending.length; i += GEO_CONCURRENCY) {
      const batch = pending.slice(i, i + GEO_CONCURRENCY);
      const results = await Promise.all(
        batch.map(async (retailer) => {
          const coords = await geocodeAddress(retailer.address);
          if (!coords) return null;
          retailer.lat = coords.lat;
          retailer.lng = coords.lng;
          cache[cacheKey(retailer.address)] = coords;
          return retailer;
        })
      );
      results.forEach((retailer) => {
        if (retailer) out.push(retailer);
      });
      writeGeoCache(cache);
      if (i + GEO_CONCURRENCY < pending.length) await sleep(GEO_DELAY_MS);
    }

    return out;
  }

  async function geocodeAddress(address) {
    if (!address) return null;

    // Photon (Komoot) — OSM-based, CORS-friendly for browser geocoding.
    // Nominatim’s public endpoint typically blocks cross-origin browser calls.
    const url = new URL("https://photon.komoot.io/api/");
    url.searchParams.set("q", address);
    url.searchParams.set("limit", "1");

    try {
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      const data = await res.json();
      const hit = data?.features?.[0];
      const coords = hit?.geometry?.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      const lng = toNumber(coords[0]);
      const lat = toNumber(coords[1]);
      if (lat == null || lng == null) return null;
      return { lat, lng };
    } catch (_err) {
      return null;
    }
  }

  function plotRetailers(items) {
    const bounds = [];

    items.forEach((retailer) => {
      const marker = L.marker([retailer.lat, retailer.lng], { icon });
      const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        retailer.address
      )}`;

      marker.bindPopup(
        `<div class="retailer-popup">
          <strong>${escapeHtml(retailer.name)}</strong>
          <p>${escapeHtml(retailer.address)}</p>
          <a href="${directions}" target="_blank" rel="noopener noreferrer">Get directions</a>
        </div>`
      );

      markersById.set(retailer.id, marker);

      if (cluster) {
        cluster.addLayer(marker);
      } else {
        marker.addTo(map);
      }

      bounds.push([retailer.lat, retailer.lng]);
    });

    plotRetailers.bounds = bounds;
  }

  function parseState(address) {
    const match = String(address).match(/,\s*([A-Z]{2})\s+\d{5}(?:-\d{4})?/);
    return match ? match[1] : "Other";
  }

  function parseCity(address) {
    const parts = String(address).split(",");
    if (parts.length < 2) return "";
    return parts[parts.length - 2].trim().replace(/\s+[A-Z]{2}$/, "");
  }

  function stateRetailerCount(citiesByCity) {
    return Object.values(citiesByCity).reduce((sum, cityRetailers) => sum + cityRetailers.length, 0);
  }

  function groupForList(items) {
    const byState = {};

    items.forEach((retailer) => {
      const state = parseState(retailer.address);
      const city = parseCity(retailer.address) || "Other";
      if (!byState[state]) byState[state] = {};
      if (!byState[state][city]) byState[state][city] = [];
      byState[state][city].push(retailer);
    });

    return Object.keys(byState)
      .sort((a, b) => {
        const countDiff = stateRetailerCount(byState[b]) - stateRetailerCount(byState[a]);
        if (countDiff !== 0) return countDiff;
        return (STATE_NAMES[a] || a).localeCompare(STATE_NAMES[b] || b);
      })
      .map((state) => ({
        state,
        cities: Object.keys(byState[state])
          .sort((a, b) => a.localeCompare(b))
          .map((city) => ({
            city,
            retailers: byState[state][city].sort((a, b) => a.name.localeCompare(b.name)),
          })),
      }));
  }

  function buildRetailerList(items) {
    if (!listEl) return;
    listEl.replaceChildren();

    const statesList = document.createElement("ul");
    statesList.className = "retailer-list__states";

    groupForList(items).forEach(({ state, cities }) => {
      const stateLabel = STATE_NAMES[state] || state;
      const stateId = `retailer-state-${slug(state)}`;
      const stateLi = document.createElement("li");
      stateLi.className = "retailer-list__state-block";

      const stateSection = document.createElement("section");
      stateSection.setAttribute("aria-labelledby", stateId);

      const stateHeading = document.createElement("h2");
      stateHeading.id = stateId;
      stateHeading.className = "retailer-list__state";
      stateHeading.textContent = stateLabel;
      stateSection.appendChild(stateHeading);

      const citiesList = document.createElement("ul");
      citiesList.className = "retailer-list__cities";

      cities.forEach(({ city, retailers: cityRetailers }) => {
        const cityId = `${stateId}-city-${slug(city)}`;
        const cityLi = document.createElement("li");
        cityLi.className = "retailer-list__city-block";

        const citySection = document.createElement("section");
        citySection.setAttribute("aria-labelledby", cityId);

        const cityHeading = document.createElement("h3");
        cityHeading.id = cityId;
        cityHeading.className = "retailer-list__city-heading";
        cityHeading.textContent = city;
        citySection.appendChild(cityHeading);

        const storesList = document.createElement("ul");
        storesList.className = "retailer-list__stores";

        cityRetailers.forEach((retailer) => {
          const storeLi = document.createElement("li");
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "retailer-list__item";
          btn.dataset.id = retailer.id;
          btn.textContent = retailer.name;
          btn.addEventListener("click", () => focusRetailer(retailer.id));
          listButtonsById.set(retailer.id, btn);
          storeLi.appendChild(btn);
          storesList.appendChild(storeLi);
        });

        citySection.appendChild(storesList);
        cityLi.appendChild(citySection);
        citiesList.appendChild(cityLi);
      });

      stateSection.appendChild(citiesList);
      stateLi.appendChild(stateSection);
      statesList.appendChild(stateLi);
    });

    listEl.appendChild(statesList);
  }

  function getMapPadding() {
    const panelOpen = DESKTOP_LIST.matches;
    return {
      paddingTopLeft: [panelOpen ? 336 : 48, 96],
      paddingBottomRight: [48, 48],
    };
  }

  function fitAllRetailers() {
    const bounds = plotRetailers.bounds || retailers.map((r) => [r.lat, r.lng]);
    if (!bounds.length) return;
    map.fitBounds(bounds, {
      ...getMapPadding(),
      maxZoom: 8,
    });
  }

  function setActiveItem(id) {
    if (activeId && listButtonsById.has(activeId)) {
      listButtonsById.get(activeId).classList.remove("is-active");
    }
    activeId = id;
    if (id && listButtonsById.has(id)) {
      listButtonsById.get(id).classList.add("is-active");
      listButtonsById.get(id).scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  function focusRetailer(id) {
    const retailer = retailers.find((r) => r.id === id);
    const marker = markersById.get(id);
    if (!retailer || !marker) return;

    setActiveItem(id);

    const onShown = () => {
      map.flyTo([retailer.lat, retailer.lng], 14, { duration: 0.5 });
      marker.openPopup();
    };

    if (cluster) {
      cluster.zoomToShowLayer(marker, onShown);
    } else {
      onShown();
    }
  }

  function cacheKey(address) {
    return String(address).trim().toLowerCase();
  }

  function readGeoCache() {
    try {
      return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}");
    } catch (_err) {
      return {};
    }
  }

  function writeGeoCache(cache) {
    try {
      localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache));
    } catch (_err) {
      /* ignore quota / private mode */
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
  } // end initMap
})();
